// zr-core/interactions — pure, framework-agnostic in-call interaction model:
// emoji reactions and a raised-hand roster. No DOM, no timers, no transport — the
// orchestrator (zr-call) feeds it inbound signaling and a monotonic `now` clock, and
// renders from the snapshots it returns. Kept pure so it is unit-testable end-to-end
// and behaves identically on web and React Native.

/** Reactions a participant can send. Curated to match the WhatsApp/Meet reaction tray. */
export const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '👏', '🙏'] as const;
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

/** A reaction is a presentational fire-and-forget event; it carries who, what, when. */
export interface Reaction {
  from: string;
  emoji: string;
  at: number;
}

/** True when `emoji` is one of the curated reactions safe to broadcast. */
export function isAllowedReaction(emoji: unknown): emoji is ReactionEmoji {
  return typeof emoji === 'string' && (REACTION_EMOJIS as readonly string[]).includes(emoji);
}

/**
 * Per-sender reaction rate limiter. A user spamming the reaction button must not
 * flood the mesh/SFU signaling plane, so each sender is allowed at most one reaction
 * per `minIntervalMs` (default 600ms — fast enough to feel live, slow enough to be safe).
 */
export class ReactionThrottle {
  private last = new Map<string, number>();
  constructor(private minIntervalMs = 600) {}

  /** Returns true if a reaction from `from` at `now` is allowed (and records it). */
  allow(from: string, now: number): boolean {
    const prev = this.last.get(from);
    if (prev !== undefined && now - prev < this.minIntervalMs) return false;
    this.last.set(from, now);
    return true;
  }

  reset(): void {
    this.last.clear();
  }
}

/**
 * Ordered raised-hand roster. Hands are queued in raise order (FIFO) so a host can
 * "call on" people fairly — the first to raise is first in the returned list. Pure:
 * every mutation returns the current ordered snapshot.
 */
export class RaisedHandRoster {
  // peerId -> the timestamp the hand was (most recently) raised.
  private raised = new Map<string, number>();

  /**
   * Set a participant's hand state. `at` is the raise time used for ordering; raising
   * an already-raised hand keeps the original position (idempotent). Returns the new
   * ordered snapshot.
   */
  set(peerId: string, isRaised: boolean, at: number): RaisedHand[] {
    if (isRaised) {
      if (!this.raised.has(peerId)) this.raised.set(peerId, at);
    } else {
      this.raised.delete(peerId);
    }
    return this.snapshot();
  }

  /** Lower every hand (e.g. host "lower all"). Returns the (empty) snapshot. */
  clear(): RaisedHand[] {
    this.raised.clear();
    return this.snapshot();
  }

  /** Drop a participant entirely (on leave) so a stale hand doesn't linger. */
  remove(peerId: string): RaisedHand[] {
    this.raised.delete(peerId);
    return this.snapshot();
  }

  isRaised(peerId: string): boolean {
    return this.raised.has(peerId);
  }

  /** Ordered by raise time ascending (longest-waiting first). */
  snapshot(): RaisedHand[] {
    return [...this.raised.entries()]
      .map(([peerId, at]) => ({ peerId, at }))
      .sort((a, b) => a.at - b.at);
  }

  get size(): number {
    return this.raised.size;
  }
}

export interface RaisedHand {
  peerId: string;
  at: number;
}
